import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, Paper, Card, CardContent, CardActions, Chip, IconButton, Tooltip,
  Avatar, Divider, Stack, LinearProgress, Alert, Snackbar, CircularProgress,
  Fade, Zoom, TextareaAutosize
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ScienceIcon from '@mui/icons-material/Science';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ImageIcon from '@mui/icons-material/Image';
import axios from 'axios';
import Loading from '../components/Loading';
import API_URL from '../config';

const FILE_FIELDS = [
  { id: 'project_location_image', label: 'Project Location Image', accept: 'image/*', required: true, icon: '🖼️' },
  { id: 'bore_hole_image',        label: 'Bore Hole Image',        accept: 'image/*', required: true, icon: '🕳️' },
  { id: 'trial_pit_image',        label: 'Trial Pit Image',        accept: 'image/*', required: true, icon: '⛏️' },
  { id: 'geological_map',         label: 'Geological Map Image',   accept: 'image/*', required: false, icon: '🗺️', ksaRequired: true },
  { id: 'gint_file',              label: 'GINT Sheet',              accept: '.xlsx,.xls,.csv', required: true, icon: '📊' },
  { id: 'gw_file',                label: 'Ground Water Sheet',      accept: '.xlsx,.xls,.csv', required: false, icon: '💧' },
  { id: 'falling_head_file',      label: 'Falling Head Sheet',      accept: '.xlsx,.xls,.csv', required: false, icon: '📉' },
  { id: 'n60_file',               label: 'N60 Sheet',               accept: '.xlsx,.xls,.csv', required: false, icon: '📋' },
  { id: 'pmt_file',               label: 'PMT Sheet',               accept: '.xlsx,.xls,.csv', required: false, icon: '📄' },
  { id: 'soakaway_file',          label: 'Soakaway Sheet',          accept: '.xlsx,.xls,.csv', required: false, icon: '📑' },
  { id: 'lab_file',               label: 'Lab Sheet',               accept: '.xlsx,.xls,.csv', required: false, icon: '🧪' },
  { id: 'packer_files',           label: 'Packer Sheets (Multiple)', accept: '.xlsx,.xls,.csv', required: false, icon: '📦', multiple: true },
];

function GeoReport() {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [files, setFiles] = useState({});
  const [dragging, setDragging] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [countryOrigin, setCountryOrigin] = useState('KSA');
  const [siteDescription, setSiteDescription] = useState('');
  const [regionalGeology, setRegionalGeology] = useState('');
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');

  const isKsa = countryOrigin === 'KSA';
  const fileInputRefs = useRef({});
  const selectedCountryName = countries.find(c => String(c.id) === String(selectedCountry))?.name || '';

  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => { fetchReports(); }, [selectedCountry]);

  useEffect(() => {
    if (selectedCountryName) {
      const name = selectedCountryName.toLowerCase();
      if (name.includes('saudi') || name.includes('ksa')) setCountryOrigin('KSA');
      else if (name.includes('qatar')) setCountryOrigin('QATAR');
    }
  }, [selectedCountryName]);

  const fetchCountries = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/setup/countries`, { headers: { Authorization: `Bearer ${token}` } });
      setCountries(res.data);
      const userCountries = JSON.parse(localStorage.getItem('user_countries') || '[]');
      if (userCountries.length > 0) {
        const matched = res.data.find(c => userCountries.some(uc => String(uc) === String(c.id) || uc === c.name));
        if (matched) setSelectedCountry(matched.id);
        else if (userCountries.length === 1) {
          const dm = res.data.find(c => String(c.id) === String(userCountries[0]));
          if (dm) setSelectedCountry(dm.id);
        }
      }
    } catch (_) {}
  };

  const fetchReports = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const params = selectedCountry ? `?country_id=${selectedCountry}` : '';
      const res = await axios.get(`${API_URL}/ai-reports/gfr-reports${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setReports(res.data.reports || []);
    } catch (err) {
      setError('Failed to load GFR reports.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setFiles({});
    setSiteDescription('');
    setRegionalGeology('');
    setDownloadUrl(null);
    setGenerating(false);
    setDragging(false);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFiles({});
    setDownloadUrl(null);
    setGenerating(false);
    setDragging(false);
  };

  const handleFileChange = (fieldId, file) => {
    setFiles(prev => { const next = { ...prev }; if (!file) delete next[fieldId]; else next[fieldId] = file; return next; });
    setDownloadUrl(null);
  };

  const countFiles = () => Object.keys(files).length;

  const handleGenerate = async () => {
    const missingRequired = FILE_FIELDS.filter(f => {
      if (f.required && !files[f.id]) return true;
      if (f.ksaRequired && isKsa && !files[f.id]) return true;
      return false;
    });
    if (missingRequired.length > 0) {
      setSnackbar({ open: true, message: `Missing: ${missingRequired.map(f => f.label).join(', ')}`, severity: 'warning' });
      return;
    }
    if (!siteDescription.trim()) { setSnackbar({ open: true, message: 'Site Description is required', severity: 'warning' }); return; }
    if (isKsa && !regionalGeology.trim()) { setSnackbar({ open: true, message: 'Regional Geology is required for KSA', severity: 'warning' }); return; }

    setGenerating(true);
    setError(null);

    const formData = new FormData();
    FILE_FIELDS.forEach(f => {
      const val = files[f.id];
      if (!val) return;
      if (f.multiple && val instanceof FileList) Array.from(val).forEach(fl => formData.append(f.id, fl));
      else if (val instanceof File) formData.append(f.id, val);
    });
    formData.append('site_description', siteDescription);
    if (regionalGeology.trim()) formData.append('regional_geology', regionalGeology);
    formData.append('country_origin', countryOrigin);
    if (selectedCountry) formData.append('country_id', selectedCountry);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/ai-reports/generate_gfr`, formData, {
        responseType: 'blob',
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      setDownloadUrl(url);
      setSnackbar({ open: true, message: 'GFR Report generated successfully!', severity: 'success' });
      try {
        await axios.post(`${API_URL}/logs`, {
          module: 'Geo Report', action: 'GENERATE', entity_id: 'GFR',
          details: `Generated GFR Report for ${countryOrigin}`, source: 'Web'
        }, { headers: { Authorization: `Bearer ${token}` } });
      } catch (_) {}
      fetchReports();
    } catch (err) {
      let errorMsg = 'Failed to generate report.';
      if (err.response?.data) {
        try { const text = await err.response.data.text(); const json = JSON.parse(text); errorMsg = json.message || errorMsg; } catch (_) {}
      }
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = () => {
    if (downloadUrl) {
      const link = document.createElement('a'); link.href = downloadUrl; link.download = 'GFR_Report.docx';
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
    }
  };

  const reportList = reports.length > 0 ? reports
    : [{ _id: 'default', name: 'GFR Report', description: 'Generate a Geo Foundation Report from field data.' }];

  if (loading && countries.length === 0) return <Loading />;

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden', boxSizing: 'border-box', pb: 6, px: { xs: 1, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>

      {/* Gradient Header */}
      <Zoom in={true} timeout={600}>
        <Box sx={{
          mb: 4, mt: 2, p: { xs: 3, md: 4 }, borderRadius: 4,
          background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 40%, #1e3c72 100%)',
          color: 'white',
          boxShadow: '0 10px 40px rgba(15, 23, 42, 0.35)',
          position: 'relative', overflow: 'hidden',
          display: 'flex', flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 3,
        }}>
          <Box sx={{ position: 'absolute', top: -60, right: -40, width: 220, height: 220, borderRadius: '50%', background: 'rgba(59,130,246,0.12)', pointerEvents: 'none' }} />
          <Box sx={{ position: 'absolute', bottom: -80, left: '20%', width: 180, height: 180, borderRadius: '50%', background: 'rgba(139,92,246,0.08)', pointerEvents: 'none' }} />
          <Box sx={{ position: 'relative', zIndex: 2 }}>
            <Typography variant="h4" fontWeight="800" sx={{ mb: 1, letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 2, color: 'white' }}>
              <AutoAwesomeIcon sx={{ fontSize: 34, color: '#60a5fa' }} />
              Geo Report
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.75)', maxWidth: 500 }}>
              Upload field data to generate AI-powered Geo Foundation Reports (GFR).
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, position: 'relative', zIndex: 2 }}>
            <Button variant="contained" startIcon={<RefreshIcon />} onClick={fetchReports}
              sx={{ borderRadius: 2.5, fontWeight: 700, px: 3, py: 1.2, bgcolor: 'rgba(255,255,255,0.15)', color: 'white', textTransform: 'none', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)', border: '1px solid rgba(255,255,255,0.25)' } }}>
              Refresh
            </Button>
          </Box>
        </Box>
      </Zoom>

      {/* Error Alert */}
      <Fade in={!!error} timeout={300}>
        <Box>{error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}</Box>
      </Fade>

      {/* Report Cards Grid */}
      <Grid container spacing={2}>
        {reportList.map((report, idx) => {
          const color = '#1F79B1';
          const gradient = `linear-gradient(135deg, ${color}, ${color}dd)`;
          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={report.id || report._id || idx}>
              <Zoom in={true} timeout={300 + idx * 60} style={{ transitionDelay: `${idx * 40}ms` }}>
                <Card elevation={0} sx={{
                  height: '100%', display: 'flex', flexDirection: 'column',
                  borderRadius: 4, bgcolor: '#ffffff',
                  border: '1px solid #f1f5f9',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative', overflow: 'hidden',
                  '&::before': { content: '""', position: 'absolute', top: 0, left: 0, width: '100%', height: '4px', background: gradient },
                  '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 18px 35px rgba(0,0,0,0.1)', borderColor: `${color}40` }
                }}>
                  <CardContent sx={{ flexGrow: 1, pb: 1, pt: 3 }}>
                    <Stack direction="row" alignItems="flex-start" spacing={1.5} mb={1.5}>
                      <Avatar variant="rounded" sx={{
                        width: 44, height: 44, borderRadius: 2.5,
                        bgcolor: `${color}15`, color: color,
                        fontSize: '1.4rem', flexShrink: 0,
                        boxShadow: `0 4px 10px ${color}20`,
                      }}>
                        📊
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', lineHeight: 1.2, mb: 0.3 }}>
                          {report.name || report.project_name || 'GFR Report'}
                        </Typography>
                        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                          <Chip label="GFR" size="small"
                            sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#f5f3ff', color: '#7c3aed' }} />
                          {(report.country_name || selectedCountryName) && (
                            <Chip label={report.country_name || selectedCountryName} size="small"
                              sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#eff6ff', color: '#2563eb' }} />
                          )}
                        </Stack>
                      </Box>
                    </Stack>
                    <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.78rem', lineHeight: 1.5 }}>
                      {report.description || report.location || report.client || 'Generate a GFR report from field data.'}
                    </Typography>
                  </CardContent>
                  <Divider sx={{ borderColor: '#f1f5f9' }} />
                  <CardActions sx={{ px: 2.5, py: 2 }}>
                    <Button fullWidth variant="contained" size="medium"
                      startIcon={<CloudUploadIcon sx={{ fontSize: 18 }} />}
                      onClick={handleOpenDialog}
                      sx={{
                        background: gradient, color: 'white',
                        borderRadius: 2.5, fontWeight: 700,
                        textTransform: 'none', fontSize: '0.82rem',
                        boxShadow: `0 4px 14px ${color}40`, py: 1,
                        '&:hover': { background: gradient, filter: 'brightness(0.88)', boxShadow: `0 6px 20px ${color}50` }
                      }}>
                      Generate Report
                    </Button>
                  </CardActions>
                </Card>
              </Zoom>
            </Grid>
          );
        })}
      </Grid>


      {/* Upload Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={(_, reason) => { if (reason !== 'backdropClick') handleCloseDialog(); }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}
        TransitionComponent={Fade}
        transitionDuration={300}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #0f172a, #1e3a5f)',
          color: 'white', py: 2.5, px: 3,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar variant="rounded" sx={{ width: 40, height: 40, borderRadius: 2.5, bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontSize: '1.3rem' }}>
              📊
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={800} color="white" lineHeight={1.2}>GFR Report</Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)' }}>Upload field data — images, Excel sheets & descriptions</Typography>
            </Box>
          </Stack>
          {/* Country Toggle */}
          <Box sx={{ bgcolor: 'rgba(255,255,255,0.12)', borderRadius: 2, p: 0.5, display: 'flex', gap: 0.5 }}>
            {['KSA', 'QATAR'].map(opt => (
              <Button key={opt} size="small"
                onClick={() => { if (!generating) { setCountryOrigin(opt); setFiles({}); } }} disabled={generating}
                sx={{ px: 2, py: 0.5, borderRadius: 1.5, fontWeight: 800, fontSize: '0.7rem', textTransform: 'none', minWidth: 44,
                  bgcolor: countryOrigin === opt ? 'white' : 'transparent',
                  color: countryOrigin === opt ? '#1e3a5f' : 'rgba(255,255,255,0.85)',
                  '&:hover': { bgcolor: countryOrigin === opt ? 'white' : 'rgba(255,255,255,0.15)' } }}>
                {opt}
              </Button>
            ))}
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 2, px: 3 }}>
          {/* File drop zones */}
          <Typography variant="caption" fontWeight={700} color="#475569" display="block" mb={1}>FIELD DATA FILES</Typography>
          <Grid container spacing={2}>
            {FILE_FIELDS.map(f => {
              const isRequired = f.required || (f.ksaRequired && isKsa);
              const file = files[f.id];
              const fileName = file ? (file instanceof FileList ? `${Array.from(file).length} files` : file.name) : null;
              return (
                <Grid item xs={6} sm={4} key={f.id}>
                  <Box
                    onDragOver={e => { e.preventDefault(); setDragging(true); }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={e => { e.preventDefault(); setDragging(false); if (e.dataTransfer.files.length > 0) handleFileChange(f.id, f.multiple ? e.dataTransfer.files : e.dataTransfer.files[0]); }}
                    onClick={() => fileInputRefs.current[f.id]?.click()}
                    sx={{
                      border: `1.5px dashed ${file ? '#86efac' : dragging ? '#3b82f6' : '#e2e8f0'}`,
                      borderRadius: 2.5, p: 1.5, textAlign: 'center', cursor: 'pointer', minHeight: 64,
                      bgcolor: file ? '#f0fdf4' : dragging ? '#f0f7ff' : '#fafbfc',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5,
                      transition: 'all 0.2s',
                      '&:hover': { borderColor: '#3b82f6', bgcolor: '#f8faff' }
                    }}
                  >
                    <input type="file" ref={el => fileInputRefs.current[f.id] = el} accept={f.accept} multiple={f.multiple || false} style={{ display: 'none' }}
                      onChange={e => { if (e.target.files.length > 0) handleFileChange(f.id, f.multiple ? e.target.files : e.target.files[0]); }} />
                    {file ? (
                      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ width: '100%' }}>
                        <InsertDriveFileIcon sx={{ color: '#10b981', fontSize: 16, flexShrink: 0 }} />
                        <Typography variant="caption" fontWeight={600} color="#166534" noWrap sx={{ flex: 1, minWidth: 0 }}>
                          {fileName}
                        </Typography>
                        <IconButton size="small" onClick={e => { e.stopPropagation(); handleFileChange(f.id, null); }}
                          sx={{ color: '#f87171', p: 0.25, flexShrink: 0 }}>
                          <DeleteIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Stack>
                    ) : (
                      <>
                        <Typography variant="caption" fontWeight={600} color={isRequired ? '#ef4444' : '#64748b'} sx={{ fontSize: '0.7rem' }}>
                          {f.label}{isRequired ? ' *' : ''}
                        </Typography>
                        <Typography variant="caption" color="#94a3b8" sx={{ fontSize: '0.65rem' }}>
                          {f.multiple ? 'Drop files / click' : 'Drop file / click'}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>

          {/* Text areas */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="caption" fontWeight={700} color="#ef4444" display="block" gutterBottom>Site Description *</Typography>
            <TextareaAutosize minRows={2} maxRows={4}
              placeholder="Enter site description..."
              value={siteDescription} onChange={e => setSiteDescription(e.target.value)} disabled={generating}
              style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.875rem', fontFamily: 'inherit', resize: 'vertical', outline: 'none', boxSizing: 'border-box' }} />
          </Box>

          {isKsa && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" fontWeight={700} color="#ef4444" display="block" gutterBottom>Regional Geology *</Typography>
              <TextareaAutosize minRows={2} maxRows={4}
                placeholder="Enter regional geology details for KSA..."
                value={regionalGeology} onChange={e => setRegionalGeology(e.target.value)} disabled={generating}
                style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.875rem', fontFamily: 'inherit', resize: 'vertical', outline: 'none', boxSizing: 'border-box' }} />
            </Box>
          )}

          {generating && (
            <Box sx={{ mt: 3, textAlign: 'center', py: 2 }}>
              <CircularProgress size={36} sx={{ color: '#1F79B1', mb: 1 }} />
              <Typography variant="body2" fontWeight={600} color="#475569">Generating your report...</Typography>
              <Typography variant="caption" color="#94a3b8">This may take a moment</Typography>
            </Box>
          )}

          {downloadUrl && !generating && (
            <Fade in={true} timeout={400}>
              <Box sx={{ mt: 3 }}>
                <Button fullWidth variant="contained" size="large" startIcon={<DownloadIcon />} onClick={handleDownload}
                  sx={{ bgcolor: '#10b981', color: 'white', borderRadius: 2.5, fontWeight: 700, textTransform: 'none', py: 1.4,
                    boxShadow: '0 4px 14px rgba(16,185,129,0.4)', '&:hover': { bgcolor: '#059669', boxShadow: '0 6px 20px rgba(16,185,129,0.5)' } }}>
                  Download Generated Report
                </Button>
              </Box>
            </Fade>
          )}
        </DialogContent>

        <DialogActions sx={{ borderTop: '1px solid #f1f5f9', px: 3, py: 2.5, bgcolor: '#f8fafc', gap: 1 }}>
          <Button onClick={handleCloseDialog} disabled={generating} sx={{ color: '#64748b', fontWeight: 600, textTransform: 'none', borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" disabled={countFiles() === 0 || generating} onClick={handleGenerate} startIcon={<AutoAwesomeIcon />}
            sx={{
              background: 'linear-gradient(135deg, #1e3a5f, #1e3c72)', color: 'white', borderRadius: 2.5, fontWeight: 700, textTransform: 'none', px: 3,
              boxShadow: '0 4px 14px rgba(30,58,95,0.35)',
              '&:hover': { filter: 'brightness(0.9)', boxShadow: '0 6px 20px rgba(30,58,95,0.45)' },
              '&.Mui-disabled': { bgcolor: '#e2e8f0', color: '#94a3b8', boxShadow: 'none' },
            }}>
            {generating ? 'Generating...' : 'Upload & Generate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2.5, fontWeight: 700, boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default GeoReport;